import { useEffect } from 'react'
import Confetti from 'react-confetti'
import { createSearchParams, Link, useSearchParams } from 'react-router'
import { usePostHog } from '@posthog/react'
import { Button } from '@heroui/react'
import { useTheme } from '@/ui/theme'
import { useTranslate, usePages } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { testOrderID } from '@/app/login/login-form'
import { resetStore } from '@/redux/store'
import { PortalFooter } from '@/ui/layout/portal-footer'
import { HoffHeader } from '@/ui/custom/hoff-header'

function SuccessTrackingPage() {
  const posthog = usePostHog()

  const { translate } = useTranslate()

  const theme = useTheme()

  const [params] = useSearchParams()
  const { pages } = usePages()

  const dispatch = useAppDispatch()

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const isKeepYourItem = useAppSelector((s) => s.return.keepYourItem)

  const webUrl = settings.website_url

  const processId = params.get('processId')
  const orderNumber = params.get('orderNumber')
  const email = params.get('email')
  const checkoutId = params.get('checkoutId')

  useEffect(() => {
    posthog?.identify(`${email}-${settings.ecommerce_id}-${orderNumber}`, {
      email,
      ecommerceId: settings.ecommerce_id,
      orderNumber: orderNumber
    })

    posthog.register({
      is_testing_user: orderNumber === testOrderID
    })

    posthog?.capture('Completed', {
      checkout_id: checkoutId,
      process_id: processId
    })

    dispatch(resetStore())
  }, [
    checkoutId,
    dispatch,
    email,
    orderNumber,
    posthog,
    processId,
    settings.ecommerce_id
  ])

  const isMisako = settings.ecommerce_group === 'misako'
  const isHoff = settings.ecommerce_group === 'hoff'

  const logoUrl = settings.branding?.logo_url
  const title = settings.branding?.title
  const mobileLogoHeight = settings.branding?.logo_height_mobile

  if (isHoff) {
    return (
      <div className="flex h-screen flex-col">
        <HoffHeader />
        <div className="relative mx-auto flex max-w-screen-xl grow flex-col items-center bg-cover bg-center bg-no-repeat px-10 pt-20">
          <div className="flex flex-col items-center">
            <p
              className="mt-16 text-xl font-light"
              dangerouslySetInnerHTML={{
                __html: translate('success_title')
              }}
            />
            <p
              className="mb-8 text-lg font-light"
              dangerouslySetInnerHTML={{
                __html: isKeepYourItem
                  ? ''
                  : translate(`tracking_success_return_processed`) + '.'
              }}
            />
            <p
              className="max-w-[1000px] text-center text-sm font-light text-neutral-500"
              dangerouslySetInnerHTML={{
                __html: isKeepYourItem
                  ? ''
                  : translate(`tracking_success_receive_email`)
              }}
            />
            {webUrl ? (
              <Button
                className="mt-10"
                variant="light"
                href={webUrl}
                fullWidth
                radius="sm"
                style={{
                  color: theme.colors.primary.main,
                  borderRadius: theme.borderRadius
                }}
              >
                <p
                  className="underline underline-offset-4"
                  dangerouslySetInnerHTML={{
                    __html: translate('success_continue_shopping')
                  }}
                />
              </Button>
            ) : null}
          </div>
        </div>
        <div className="mx-auto mb-5 mt-auto">
          <PortalFooter />
        </div>
      </div>
    )
  }

  if (isMisako) {
    return (
      <div className="flex h-full flex-1 flex-col justify-center gap-4 p-4 md:px-10 md:py-6">
        <div className="mb-auto flex w-full items-end">
          {logoUrl ? (
            <div className="mr-auto">
              <img
                style={
                  {
                    '--logo-desktop-height': '1.5rem',
                    '--logo-mobile-height': mobileLogoHeight ?? '1.5rem'
                  } as React.CSSProperties
                }
                width={285}
                height={70}
                src={logoUrl}
                alt={title}
                className="h-[var(--logo-mobile-height)] w-auto object-contain md:h-[var(--logo-desktop-height)]"
              />
            </div>
          ) : null}
          <div className="flex items-center gap-5">
            <a
              href="https://www.misako.com/pages/wishlist"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                width={20}
                height={20}
              />
            </a>
            <a
              href="https://www.misako.com/account"
              target="_blank"
              rel="noreferrer"
            >
              <svg
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                  stroke="rgb(21, 21, 21)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                  stroke="rgb(21, 21, 21)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </a>
            <a
              href="https://www.misako.com/collections/top-ventas"
              target="_blank"
              rel="noreferrer"
            >
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                  stroke="rgb(21, 21, 21)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </a>
          </div>
        </div>

        <div className="mt-24 flex-1">
          <h3
            className="font-[400] tracking-wider"
            dangerouslySetInnerHTML={{
              __html: translate('success_title')
            }}
          />

          <h6
            className="mb-8 mt-2 font-[300] tracking-wider"
            dangerouslySetInnerHTML={{
              __html: isKeepYourItem
                ? ''
                : translate(`tracking_success_return_processed`) + '.'
            }}
          />
          <p
            className="text-[15px] font-[300] tracking-wider"
            dangerouslySetInnerHTML={{
              __html: isKeepYourItem
                ? ''
                : translate(`tracking_success_receive_email`)
            }}
          />
          {webUrl ? (
            <Button
              className="mt-10"
              variant="light"
              href={webUrl}
              fullWidth
              radius="sm"
              style={{
                color: theme.colors.primary.main,
                borderRadius: theme.borderRadius
              }}
            >
              <p
                className="text-[13px] uppercase tracking-wider underline underline-offset-8"
                dangerouslySetInnerHTML={{
                  __html: translate('success_continue_shopping')
                }}
              />
            </Button>
          ) : null}
        </div>
        <div className="mb-5 mt-auto">
          <PortalFooter />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-screen flex-1 flex-col items-center justify-center p-8 text-center lg:bg-[#f2f2f2]">
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="flex h-fit flex-col items-center justify-center bg-white p-4 lg:rounded-lg lg:px-8 lg:py-6 lg:shadow-sm">
          <Confetti initialVelocityY={20} recycle={false} id="confetti" />
          <h3
            className="text-4xl font-extrabold"
            dangerouslySetInnerHTML={{
              __html: translate('success_title')
            }}
          />

          <h6
            className="mb-8 mt-2 text-xl font-extrabold"
            dangerouslySetInnerHTML={{
              __html: isKeepYourItem
                ? ''
                : translate(`tracking_success_return_processed`) + '.'
            }}
          />

          <p
            className="pb-8"
            dangerouslySetInnerHTML={{
              __html: translate(`tracking_success_click_button`)
            }}
          />

          <Button
            data-testid="return-instructions-link"
            as={Link}
            to={{
              pathname: pages.Tracking,
              search: createSearchParams({
                processId: processId ?? '',
                orderNumber: orderNumber ?? '',
                email: email ?? '',
                source: 'success_page'
              }).toString()
            }}
            replace
            variant="solid"
            radius="sm"
            size="lg"
            style={{
              backgroundColor: theme.colors.primary.main,
              color: theme.colors.primary.contrastText,
              borderRadius: theme.borderRadius
            }}
          >
            {isKeepYourItem
              ? translate('tracking_success_kyi_action_button')
              : translate('tracking_success_see_return_instructions')}
          </Button>
          <p
            className="pt-8"
            dangerouslySetInnerHTML={{
              __html: isKeepYourItem
                ? ''
                : translate(`tracking_success_receive_email`)
            }}
          />
          {webUrl ? (
            <Button
              className="mt-10"
              variant="light"
              href={webUrl}
              fullWidth
              radius="sm"
              style={{
                color: theme.colors.primary.main,
                borderRadius: theme.borderRadius
              }}
            >
              <p
                className="underline underline-offset-4"
                dangerouslySetInnerHTML={{
                  __html: translate('success_continue_shopping')
                }}
              />
            </Button>
          ) : null}
        </div>
      </div>
      <PortalFooter />
    </div>
  )
}

export default SuccessTrackingPage
